import { $t } from '../../composables/i18n'
import { CountryId } from '../country/CountryId'
import { Codebook } from '../Codebook'

export enum PaymentMethodId {
  ONSITE = 1,
  BANK_TRANSFER = 2,
  TERMINAL = 3,
  CASH = 4,
  COUPON = 5,
  COD = 6,
  PAYPAL = 7,
  GUARANTEE = 8,
  PAYU = 9,
  ONLINE = 10,
  SZEP_CARD_TRANSFER_OTP = 11,
  SZEP_CARD_TRANSFER_MBH = 12,
  SZEP_CARD_TRANSFER_KH = 13,
  MIXED = 100,
}

export class PaymentMethod extends Codebook<PaymentMethodId> {
  static readonly ONSITE = new PaymentMethod(
    PaymentMethodId.ONSITE,
    () => $t('On site'),
    () => $t('ONS'),
    false,
    null,
    false,
  )
  static readonly BANK_TRANSFER = new PaymentMethod(
    PaymentMethodId.BANK_TRANSFER,
    () => $t('Bank transfer'),
    () => $t('BAN'),
    true,
    null,
    false,
  )
  static readonly TERMINAL = new PaymentMethod(
    PaymentMethodId.TERMINAL,
    () => $t('Payment terminal'),
    () => $t('TER'),
    true,
    null,
    true,
  )
  static readonly CASH = new PaymentMethod(
    PaymentMethodId.CASH,
    () => $t('Cash'),
    () => $t('CAS'),
    true,
    null,
    true,
  )
  static readonly COUPON = new PaymentMethod(
    PaymentMethodId.COUPON,
    () => $t('Coupon'),
    () => $t('COU'),
    true,
    null,
    false,
  )
  static readonly COD = new PaymentMethod(
    PaymentMethodId.COD,
    () => $t('COD'),
    () => $t('COD'),
    false,
    null,
    false,
  )
  static readonly PAYPAL = new PaymentMethod(
    PaymentMethodId.PAYPAL,
    () => $t('PayPal'),
    () => $t('PAP'),
    false,
    null,
    false,
  )
  static readonly GUARANTEE = new PaymentMethod(
    PaymentMethodId.GUARANTEE,
    () => $t('Guarantee'),
    () => $t('GAR'),
    false,
    null,
    false,
  )
  static readonly PAYU = new PaymentMethod(
    PaymentMethodId.PAYU,
    () => $t('Payu'),
    () => $t('PAU'),
    false,
    null,
    false,
  )
  static readonly ONLINE = new PaymentMethod(
    PaymentMethodId.ONLINE,
    () => $t('Online payment'),
    () => $t('ONL'),
    true,
    null,
    true,
  )
  static readonly SZEP_CARD_TRANSFER_OTP = new PaymentMethod(
    PaymentMethodId.SZEP_CARD_TRANSFER_OTP,
    () => $t('Transfer of OTP SZÉP card'),
    () => $t('OSZ'),
    true,
    CountryId.HUN,
    false,
  )
  static readonly SZEP_CARD_TRANSFER_MBH = new PaymentMethod(
    PaymentMethodId.SZEP_CARD_TRANSFER_MBH,
    () => $t('Transfer of MBH SZÉP card'),
    () => $t('MSZ'),
    true,
    CountryId.HUN,
    false,
  )
  static readonly SZEP_CARD_TRANSFER_KH = new PaymentMethod(
    PaymentMethodId.SZEP_CARD_TRANSFER_KH,
    () => $t('Transfer of K&H SZÉP card'),
    () => $t('KSZ'),
    true,
    CountryId.HUN,
    false,
  )

  protected constructor(
    id: PaymentMethodId,
    public readonly name: () => string,
    public readonly shortName: () => string,
    public readonly isForInvoice: boolean,
    public readonly countryId: null | CountryId, // null = for all countries
    public readonly isImmediateSettlement: boolean,
  ) {
    super(id)
  }

  static allForInvoiceAndByCountryId = (countryId: number): PaymentMethod[] => {
    return PaymentMethod.all().filter(
      (method) =>
        method.isForInvoice && (method.countryId === null || method.countryId === countryId),
    )
  }

  static hasElectronicPaymentType = (paymentMethodId: PaymentMethodId | null): boolean => {
    return (
      paymentMethodId === PaymentMethodId.TERMINAL || paymentMethodId === PaymentMethodId.ONLINE
    )
  }
}

/**
 * Extends {@link PaymentMethod} with the synthetic `MIXED` value, used in contexts
 * where a payment can be settled by a combination of methods.
 */
export class PaymentMethodWithMixed extends PaymentMethod {
  static readonly MIXED = new PaymentMethodWithMixed(
    PaymentMethodId.MIXED,
    () => $t('Mixed'),
    () => $t('MIX'),
    true,
    null,
    false,
  )

  static allForInvoiceAndByCountryId = (countryId: number): PaymentMethod[] => {
    return [...PaymentMethod.allForInvoiceAndByCountryId(countryId), PaymentMethodWithMixed.MIXED]
  }
}
